import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Building2, UserCog, ShieldCheck, BookOpen,
  AlertTriangle, CheckCircle2, XCircle, Clock, FileQuestion,
  Plus, Bell, Sparkles, ArrowRight,
} from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

const cardColors = {
  valid: { bg: "bg-emerald-50", text: "text-emerald-700", icon: "text-emerald-600", border: "border-emerald-200" },
  warning: { bg: "bg-amber-50", text: "text-amber-700", icon: "text-amber-600", border: "border-amber-200" },
  expired: { bg: "bg-rose-50", text: "text-rose-700", icon: "text-rose-600", border: "border-rose-200" },
  missing: { bg: "bg-gray-50", text: "text-gray-700", icon: "text-gray-500", border: "border-gray-200" },
};

const Icons = {
  valid: CheckCircle2, warning: Clock, expired: XCircle, missing: FileQuestion,
};

function StatusCard({ status, count, type, t }) {
  const c = cardColors[status];
  const Icon = Icons[status];
  const labelKey = `status_${status}`;
  return (
    <Link
      to={`/employees?status=${status}&cert=${type}`}
      className={`stat-card card-hover border ${c.bg} ${c.border}`}
      data-testid={`stat-${type}-${status}`}
    >
      <div className="flex items-center justify-between mb-2">
        <Icon size={20} className={c.icon} />
        <span className={`pill ${c.bg} ${c.text}`}>{t(labelKey)}</span>
      </div>
      <div className={`text-3xl font-bold ${c.text}`}>{count}</div>
    </Link>
  );
}

function SupervisorWelcome({ user, alertsTotal, t }) {
  return (
    <div className="bg-gradient-to-br from-teal-600 to-emerald-700 text-white rounded-2xl p-5 sm:p-6 card-shadow" data-testid="supervisor-welcome">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-xs text-teal-100 mb-1">{t("welcome_back")}</p>
          <h2 className="text-xl sm:text-2xl font-bold truncate">{user.full_name || user.username} 👋</h2>
          <p className="text-sm text-teal-100 mt-1">{t("supervisor_dashboard_subtitle")}</p>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          <Link to="/employees" state={{ openAdd: true }} className="bg-white/15 hover:bg-white/25 backdrop-blur rounded-xl px-3 py-2 text-sm font-semibold flex items-center gap-1.5" data-testid="quick-add-employee">
            <Plus size={14} /> {t("add_employee")}
          </Link>
          <Link to="/employees?status=warning&cert=health" className="bg-white/15 hover:bg-white/25 backdrop-blur rounded-xl px-3 py-2 text-sm font-semibold flex items-center gap-1.5 relative" data-testid="quick-view-alerts">
            <Bell size={14} /> {t("alerts")}
            {alertsTotal > 0 && (
              <span className="bg-rose-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none">{alertsTotal}</span>
            )}
          </Link>
        </div>
      </div>
    </div>
  );
}

function ExpiringSoonList({ items, t }) {
  if (!items || items.length === 0) {
    return (
      <div className="bg-white rounded-2xl card-shadow p-6 text-center" data-testid="expiring-empty">
        <Sparkles className="mx-auto text-emerald-500 mb-2" size={28} />
        <p className="text-emerald-700 font-semibold">{t("no_expiring_soon")}</p>
        <p className="text-xs text-gray-500 mt-1">{t("all_certs_current")}</p>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl card-shadow overflow-hidden" data-testid="expiring-soon-list">
      <div className="bg-amber-50 border-b border-amber-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-amber-900">
          <Clock size={16} />
          <h3 className="font-bold text-sm">{t("expiring_soon_title")}</h3>
        </div>
        <Link to="/employees?status=warning&cert=health" className="text-xs text-amber-800 hover:underline font-semibold flex items-center gap-1">
          {t("view_all")} <ArrowRight size={12} />
        </Link>
      </div>
      <ul className="divide-y divide-gray-100">
        {items.slice(0, 5).map((e, idx) => (
          <li key={`${e.employee_id}-${e.cert_type}-${idx}`} className="px-4 py-2.5 hover:bg-gray-50" data-testid={`expiring-row-${e.employee_id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-gray-900 truncate">{e.name}</p>
                <p className="text-[11px] text-gray-500 truncate">{e.branch_name || "—"}</p>
              </div>
              <div className="shrink-0 text-end">
                <span className={`pill ${e.days < 0 ? "status-expired" : "status-warning"}`}>
                  {e.cert_type === "training" ? t("training") : t("health_cert_status")}: {e.days < 0 ? `${Math.abs(e.days)} ${t("days_short")} ${t("expired_short")}` : `${e.days} ${t("days_short")}`}
                </span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useT();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [alertsData, setAlertsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isSupervisor = user?.role === "supervisor";

  useEffect(() => {
    (async () => {
      try {
        const [dash, alerts] = await Promise.all([
          api.get("/dashboard"),
          api.get("/alerts").catch(() => ({ data: { total: 0, health: { expired: [], warning: [] } } })),
        ]);
        setData(dash.data);
        setAlertsData(alerts.data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-center text-gray-500 py-20" data-testid="dashboard-loading">{t("loading")}</div>;
  if (!data) return null;

  // Merge expired + warning items, sort by days ascending (most urgent first).
  const expiringList = alertsData ? [
    ...(alertsData.expired || []),
    ...(alertsData.warning || []),
  ].sort((a, b) => (a.days ?? 0) - (b.days ?? 0)) : [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {isSupervisor && (
        <SupervisorWelcome user={user} alertsTotal={alertsData?.total || 0} t={t} />
      )}

      {!isSupervisor && (
        <div>
          <h2 className="text-2xl font-bold text-teal-900">{t("nav_dashboard")}</h2>
          <p className="text-gray-500 text-sm">{t("dashboard_subtitle")}</p>
        </div>
      )}

      {/* Summary cards */}
      <div className={`grid gap-4 ${isSupervisor ? "grid-cols-2 md:grid-cols-2" : (user?.role === "super_admin" ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-5" : "grid-cols-2 md:grid-cols-4")}`}>
        <Link to="/employees" className="stat-card card-hover border" data-testid="stat-total">
          <div className="flex items-center justify-between mb-2">
            <Users className="text-teal-600" size={22} />
            <span className="text-xs text-gray-400">{t("total")}</span>
          </div>
          <div className="text-3xl font-bold text-teal-800">{data.total}</div>
          <p className="text-sm text-gray-500 mt-1">{t("total_employees")}</p>
        </Link>

        {!isSupervisor && (
          <>
            <Link to="/branches" className="stat-card card-hover border" data-testid="stat-branches">
              <div className="flex items-center justify-between mb-2">
                <Building2 className="text-indigo-600" size={22} />
              </div>
              <div className="text-3xl font-bold text-indigo-700">{data.branches}</div>
              <p className="text-sm text-gray-500 mt-1">{t("branches_count")}</p>
            </Link>
            <Link to="/admins" className="stat-card card-hover border" data-testid="stat-admins">
              <div className="flex items-center justify-between mb-2">
                <UserCog className="text-purple-600" size={22} />
              </div>
              <div className="text-3xl font-bold text-purple-700">{data.admins}</div>
              <p className="text-sm text-gray-500 mt-1">{t("admins_count")}</p>
            </Link>
            {user?.role === "super_admin" && typeof data.area_managers === "number" && (
              <Link to="/area-managers" className="stat-card card-hover border bg-rose-50 border-rose-200" data-testid="stat-area-managers">
                <div className="flex items-center justify-between mb-2">
                  <Sparkles className="text-rose-600" size={22} />
                </div>
                <div className="text-3xl font-bold text-rose-700">{data.area_managers}</div>
                <p className="text-sm text-rose-700 mt-1">{t("nav_area_managers")}</p>
              </Link>
            )}
          </>
        )}

        {isSupervisor && (
          <div className="stat-card border bg-indigo-50 border-indigo-200">
            <div className="flex items-center justify-between mb-2">
              <Building2 className="text-indigo-600" size={22} />
            </div>
            <div className="text-3xl font-bold text-indigo-700">{data.branches}</div>
            <p className="text-sm text-indigo-700 mt-1">{t("my_branches")}</p>
          </div>
        )}

        <div className="stat-card border bg-amber-50 border-amber-200">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className="text-amber-600" size={22} />
          </div>
          <div className="text-3xl font-bold text-amber-700">
            {data.health.expired + data.health.warning + data.training.expired + data.training.warning}
          </div>
          <p className="text-sm text-amber-700 mt-1">{t("alerts_count")}</p>
        </div>
      </div>

      {/* Supervisor: quick expiring-soon list */}
      {isSupervisor && (
        <ExpiringSoonList items={expiringList} t={t} />
      )}

      {/* Health certificate */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="text-teal-600" size={20} />
          <h3 className="text-lg font-bold text-teal-900">{t("health_cert_status")}</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {["valid", "warning", "expired", "missing"].map((s) => (
            <StatusCard key={s} status={s} count={data.health[s]} type="health" t={t} />
          ))}
        </div>
      </section>

      {/* Training */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="text-teal-600" size={20} />
          <h3 className="text-lg font-bold text-teal-900">{t("training_status")}</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {["valid", "warning", "expired", "missing"].map((s) => (
            <StatusCard key={s} status={s} count={data.training[s]} type="training" t={t} />
          ))}
        </div>
      </section>
    </div>
  );
}
